import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

// THE APPROVAL RAIL (Studio Blueprint 05-B). How it works, drawn the same
// way on home, every studio and every quick tool: free steps in teal, the one
// step where money moves in amber, so the colour alone says when you pay.
//
//   studio: Upload → Preview → You approve → Render (charged here) → Delivered
//   quick:  Upload → Preview → You decide  → Pay (charged here)    → Download
//   storybook: Add photos → Preview → Buy the book (charged here) → You
//           approve the pictures → Download or ship (2026-10-16). The parent
//           may also pay before the preview, so the note says that.
//
// The note under it is a promise the code keeps: studio renders that fail put
// their credits back; a quick tool charges nothing until the buyer pays.

export type RailKind = 'studio' | 'quick' | 'storybook';

const STEPS: Record<RailKind, readonly { key: string; paid?: boolean }[]> = {
  studio: [{ key: 'upload' }, { key: 'preview' }, { key: 'approve' }, { key: 'render', paid: true }, { key: 'delivered' }],
  quick: [{ key: 'upload' }, { key: 'preview' }, { key: 'decide' }, { key: 'pay', paid: true }, { key: 'download' }],
  storybook: [{ key: 'upload' }, { key: 'preview' }, { key: 'buy', paid: true }, { key: 'approve' }, { key: 'delivered' }],
};

export function ApprovalRail({ kind = 'studio', title = true, className }: { kind?: RailKind; title?: boolean; className?: string }) {
  const { t } = useTranslation();
  const steps = STEPS[kind];
  return (
    <section aria-labelledby={title ? `rail-${kind}` : undefined} aria-label={title ? undefined : t('trust.rail.title')} className={cn('mx-auto max-w-5xl', className)} data-testid={`approval-rail-${kind}`}>
      {title && (
        <h2 id={`rail-${kind}`} className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">
          {t('trust.rail.title')}
        </h2>
      )}
      <ol className="grid gap-2 sm:grid-cols-5 sm:gap-0">
        {steps.map((step, i) => (
          <li key={step.key} className="relative flex items-center gap-3 sm:flex-col sm:gap-2 sm:text-center">
            {/* The line between the dots (sm+): amber into and out of the paid step. */}
            {i > 0 && (
              <span
                aria-hidden
                className={cn(
                  'absolute top-[14px] hidden h-px w-full -translate-x-1/2 sm:block',
                  step.paid || steps[i - 1].paid ? 'bg-primary/50' : 'bg-accent/30',
                )}
                style={{ left: 0 }}
              />
            )}
            <span
              aria-hidden
              className={cn(
                'relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold tabular-nums ring-1 ring-inset',
                step.paid ? 'bg-primary text-primary-foreground ring-primary' : 'bg-accent/10 text-accent ring-accent/40',
              )}
            >
              {i + 1}
            </span>
            <span className="flex flex-col sm:items-center">
              <span className="text-[15px] font-semibold text-foreground">{t(`trust.rail.${kind}.${step.key}`)}</span>
              <span className={cn('text-[13px] font-medium', step.paid ? 'text-primary' : 'text-accent')}>
                {t(step.paid ? 'trust.rail.chargedHere' : i === steps.length - 1 ? 'trust.rail.yours' : 'trust.rail.free')}
              </span>
            </span>
          </li>
        ))}
      </ol>
      <p className="mt-4 text-center text-[13px] text-muted-foreground">{t(`trust.rail.${kind}.note`)}</p>
    </section>
  );
}
